import { createFileRoute } from "@tanstack/react-router";
import { ClosingCTA } from "@/components/landing/ClosingCTA";
import { FaqSection, faqSchemaScript, type FaqItem } from "@/components/landing/FaqSection";
import { Footer } from "@/components/landing/Footer";
import { Header } from "@/components/landing/Header";
import { PageIntro } from "@/components/landing/PageIntro";
import { ParticleBackground } from "@/components/landing/ParticleBackground";
import { ProcessSection } from "@/components/landing/ProcessSection";
import { RelatedServices } from "@/components/landing/RelatedServices";
import { freshnessMeta, webPageSchemaScript } from "@/lib/page-freshness";
import { serviceSchemaScripts } from "@/lib/service-schema";

const PUBLISHED = "2026-09-26T00:00:00+00:00";
const MODIFIED = "2026-09-26T00:00:00+00:00";
const TITLE = "SEO & AI Visibility for B2B Companies | Square Migration";
const DESCRIPTION = "Improve how your B2B company appears in Google and AI-generated answers with technical SEO, structured data, citation-ready content and clear measurement.";

const FAQS: FaqItem[] = [
  {
    question: "What is AI visibility?",
    answer: "AI visibility is how often your company, expertise and pages appear as relevant sources in AI-generated answers. It depends on clear site structure, strong entity signals, crawlable content and pages that directly answer the questions buyers ask.",
  },
  {
    question: "Is AI visibility different from SEO?",
    answer: "The foundations overlap, but the outcomes differ. SEO helps pages rank in search results. AI visibility also helps systems understand, retrieve and cite your expertise when generating an answer. We improve both together rather than treating them as separate channels.",
  },
  {
    question: "What does an SEO and AI visibility engagement include?",
    answer: "We review technical indexing signals, metadata, internal links, structured data, crawler access, content coverage and citation readiness. You receive prioritized fixes, implementation support and measurement focused on the buyer questions that matter to your business.",
  },
  {
    question: "Can you guarantee rankings or AI citations?",
    answer: "No responsible provider can guarantee either. Search engines and AI platforms control their own results. We improve the signals, content and technical foundations that make your business easier to discover, understand and cite.",
  },
];

const OFFERINGS = [
  {
    number: "01",
    title: "Search Foundations",
    body: "Make every important page easy for search engines to crawl, understand and index.",
    points: ["Technical indexing review", "Metadata & canonical signals", "Internal linking & site structure"],
  },
  {
    number: "02",
    title: "AI-Ready Content",
    body: "Turn your expertise into direct, useful answers that search and AI systems can retrieve with confidence.",
    points: ["Buyer-question coverage", "Citation-friendly page structure", "Entity & expertise signals"],
  },
  {
    number: "03",
    title: "Structured Signals",
    body: "Give machines a clearer picture of your company, services, authors and current content.",
    points: ["Structured data", "AI crawler readiness", "Freshness & source clarity"],
  },
];

export const Route = createFileRoute("/seo-ai-visibility")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: "https://www.squaremigration.com/seo-ai-visibility" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:image", content: "https://www.squaremigration.com/og-image.jpg" },
      { name: "twitter:image", content: "https://www.squaremigration.com/og-image.jpg" },
      ...freshnessMeta({ published: PUBLISHED, modified: MODIFIED }),
    ],
    links: [{ rel: "canonical", href: "https://www.squaremigration.com/seo-ai-visibility" }],
    scripts: [
      ...serviceSchemaScripts({ path: "/seo-ai-visibility", name: "SEO & AI Visibility", description: DESCRIPTION, serviceType: "B2B SEO and AI visibility services" }),
      faqSchemaScript(FAQS),
      webPageSchemaScript({ path: "/seo-ai-visibility", name: TITLE, description: DESCRIPTION, published: PUBLISHED, modified: MODIFIED }),
    ],
  }),
  component: SeoAiVisibilityPage,
});

function SeoAiVisibilityPage() {
  return (
    <main className="relative">
      <ParticleBackground />
      <Header />
      <PageIntro
        eyebrow="SEO & AI Visibility"
        title="Help buyers find you in search, and in the answers AI gives them."
        description="We strengthen the technical signals, content and site structure that help Google and AI platforms understand your expertise, surface your pages and cite your business when buyers ask relevant questions."
        bullets={["Technical SEO", "Structured data", "AI crawler readiness", "Citation-ready content", "Visibility measurement"]}
      />

      <section className="border-t border-border/50 bg-background/95 px-6 py-16 md:px-8 md:py-20 lg:px-12">
        <div className="mx-auto max-w-6xl">
          <div className="mb-10 grid items-start gap-6 lg:mb-14 lg:grid-cols-[0.9fr_1.1fr] lg:gap-20">
            <div>
              <p className="mb-4 text-xs font-semibold uppercase tracking-[0.22em] text-primary">What We Improve</p>
              <h2 className="text-3xl font-semibold leading-tight sm:text-4xl">One clear foundation for <span className="text-primary">search and AI discovery.</span></h2>
            </div>
            <p className="leading-relaxed text-muted-foreground lg:pt-11 lg:text-lg">Visibility does not come from adding a few keywords or chasing every new platform. It comes from useful answers, clear technical signals and a site that consistently shows who you help and what you know.</p>
          </div>

          <div className="grid gap-6 md:grid-cols-3 lg:gap-8">
            {OFFERINGS.map((item) => (
              <article key={item.title} className="flex flex-col rounded-lg border border-border/50 bg-card/30 p-6">
                <span className="mb-6 text-sm font-semibold tabular-nums text-primary">{item.number}</span>
                <h3 className="mb-3 text-xl font-semibold">{item.title}</h3>
                <p className="mb-6 leading-relaxed text-muted-foreground">{item.body}</p>
                <ul className="mt-auto space-y-3 border-t border-border/50 pt-6">
                  {item.points.map((point) => (
                    <li key={point} className="flex items-center gap-2 text-sm text-foreground/90"><span className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary" aria-hidden="true" />{point}</li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </div>
      </section>

      <ProcessSection
        eyebrow="How It Works"
        title="From visibility gaps to a stronger, measurable presence."
        lead="We prioritize the changes most likely to improve discovery, then make the technical and content work understandable and actionable."
        steps={[
          { number: "01", title: "Map Buyer Questions", description: "Identify what your best prospects search for and ask AI tools before they contact a provider." },
          { number: "02", title: "Audit the Signals", description: "Review indexing, page structure, metadata, structured data, internal links and AI crawler access." },
          { number: "03", title: "Improve the Answers", description: "Strengthen existing pages and build focused content where important buyer questions are not answered clearly." },
          { number: "04", title: "Measure Visibility", description: "Track search discovery, indexing and AI mentions so the next priorities are based on evidence rather than guesswork." },
        ]}
      />

      <ClosingCTA
        eyebrow="Improve Your Visibility"
        headline={<>Are buyers finding your expertise <span className="text-primary">before they find a competitor?</span></>}
        subhead="Let’s find the gaps."
        body="Bring your site and the questions your prospects ask most often. We’ll identify where search and AI visibility can improve and what to tackle first."
        ctaLabel="Review Your Visibility"
      />
      <FaqSection items={FAQS} />
      <RelatedServices current="/seo-ai-visibility" />
      <Footer />
    </main>
  );
}
